import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { UserCard } from 'types/interfaces/user/UserCard';

import { getRandomItem } from 'helpers/array';
import { useMyVisitsPopupQuery } from 'hooks/queries/useMyVisitsPopupQuery';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Spinner, SpinnerTypes } from 'components/base/Spinner';
import { UserAvatar } from 'components/shared/UserAvatar';
import { Popup } from 'components/UI/Popup';

import css from './myVisitsPopup.module.sass';

type ActionsPayload =
  | BasePopupActionPayload<'mount'>
  | (BasePopupActionPayload<'submit'> & { user: UserCard })
  | BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const MyVisitsPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation('app');
  const myVisitsPopupQuery = useMyVisitsPopupQuery();

  const users = myVisitsPopupQuery.data?.visits || [];
  const usersCount = users.length;
  const randomUser = getRandomItem(users);

  useEffect(() => {
    onAction({ type: 'mount' });
  }, [onAction]);

  return (
    <Popup onClose={() => onAction({ type: 'close' })}>
      <p className={css.title}>{t('retentionFunnels.myVisitsPopup.title')}</p>
      <p className={css.description}>
        <Trans
          i18nKey="retentionFunnels.myVisitsPopup.description"
          ns="app"
          values={{
            count: usersCount,
          }}
          components={{
            b: <b className={css.count} />,
          }}
        />
      </p>
      {myVisitsPopupQuery.isLoading && (
        <Spinner type={SpinnerTypes.Dark} className={css.spinner} />
      )}
      {myVisitsPopupQuery.isSuccess && (
        <ul className={css.usersList}>
          {users.map((user) => {
            return (
              <li key={user.id} className={css.usersItem}>
                <UserAvatar url={user.photo_url} size="50px" blurSize="2px" />
                <span className={css.username}>
                  {`${user.username}, ${user.age}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <BaseButton
        type={ButtonTypes.Accent}
        className={css.submit}
        onClick={() => onAction({ type: 'submit', user: randomUser })}
      >
        {t('retentionFunnels.myVisitsPopup.submit')}
      </BaseButton>
    </Popup>
  );
};
